<div class="fundraising-comparison">
  <div class="resource-surface rounded-4 bg-white border p-3 p-md-4">
    <div *ngIf="errorMessage" class="alert alert-danger mb-0" role="alert">
      {{ errorMessage }}
    </div>

    <ng-container *ngIf="result && scenario">
      <app-fundraiser-scenario
        [scenario]="scenario"
        [donationCountOverride]="state.donationCountOverride"
        (intendedGiftTotalCentsChange)="onIntendedGiftTotalCentsChange($event)"
        (donationCountOverrideChange)="onDonationCountOverrideChange($event)"
      ></app-fundraiser-scenario>

      <hr class="my-4" />

      <section [attr.aria-labelledby]="'platform-result-' + result.platformId">
        <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
          <p class="text-uppercase small fw-semibold text-primary mb-0">
            Estimated fundraiser result
          </p>
          <span
            class="badge border"
            [ngClass]="evidenceBadgeClasses(result.evidence.status)"
          >
            {{ result.evidence.label }}
          </span>
        </div>
        <h2
          [id]="'platform-result-' + result.platformId"
          class="h4 fw-bold mb-1"
        >
          Organization receives
        </h2>
        <p class="display-5 fw-bold text-primary mb-1">
          {{ formatMoney(organizationReceivesCents) }}
        </p>
        <p class="text-body-secondary mb-4">
          of {{ formatMoney(result.intendedGiftTotalCents) }} in
          {{ grossAmountContext }}
        </p>

        <div class="row g-3 mb-4">
          <div *ngIf="usesRevenueShare" class="col-12 col-sm-4">
            <p class="small text-body-secondary mb-1">Organization keeps</p>
            <p class="h5 fw-bold mb-0">
              {{ formatPercentage(result.organizationRetainedBasisPoints) }}
            </p>
          </div>
          <div
            class="col-12"
            [class.col-sm-4]="usesRevenueShare"
            [class.col-sm-6]="!usesRevenueShare"
          >
            <p class="small text-body-secondary mb-1">
              {{ organizationCostLabel }}
            </p>
            <p class="h5 fw-bold mb-0">
              <ng-container *ngIf="usesRevenueShare">
                {{
                  formatPercentage(result.effectiveOrganizationCostBasisPoints)
                }}
                ·
              </ng-container>
              {{ formatMoney(result.organizationCostCents) }}
            </p>
          </div>
          <div
            class="col-12"
            [class.col-sm-4]="usesRevenueShare"
            [class.col-sm-6]="!usesRevenueShare"
          >
            <p class="small text-body-secondary mb-1">Supporters pay extra</p>
            <p class="h5 fw-bold mb-0">{{ supporterExtraValue(result) }}</p>
          </div>
        </div>

        <p
          *ngIf="costsExceedDonationsCents > 0"
          class="alert alert-warning small"
        >
          Costs exceed donations by
          {{ formatMoney(costsExceedDonationsCents) }}.
        </p>

        <div class="border rounded-3 bg-light p-3 mb-3">
          <p class="small fw-semibold text-uppercase text-body-secondary mb-1">
            How pricing works
          </p>
          <p class="fw-bold mb-1">{{ result.compactFeeFormula }}</p>
          <p *ngIf="configurationSummary" class="small mb-0">
            {{ configurationSummary }}
          </p>
          <p
            *ngIf="selectedConfigurationDescription"
            class="small text-body-secondary mb-0 mt-1"
          >
            {{ selectedConfigurationDescription }}
          </p>
          <p
            *ngIf="result.configurationUi?.contextNote"
            class="small text-body-secondary mb-0 mt-1"
          >
            {{ result.configurationUi?.contextNote }}
          </p>
        </div>

        <section
          *ngIf="showConfigurationControls"
          class="border-top pt-4 mt-4"
          [attr.aria-labelledby]="'configuration-controls-' + result.platformId"
        >
          <h3
            [id]="'configuration-controls-' + result.platformId"
            class="h4 fw-bold mb-2"
          >
            Pricing source or agreement
          </h3>
          <app-platform-options
            [platformId]="result.platformId"
            [optionGroups]="result.optionGroups"
            [selections]="result.optionSelections"
            [configurationUi]="result.configurationUi!"
            [showSelectedSummary]="false"
            presentation="expanded"
            (optionChange)="onOptionChange($event)"
          ></app-platform-options>
        </section>

        <section
          *ngIf="result.tieredPercentageScale as scale"
          class="border rounded-3 overflow-hidden mt-4"
          [attr.aria-labelledby]="'tiered-scale-' + result.platformId"
        >
          <div class="bg-light p-3 p-md-4">
            <p class="text-uppercase small fw-semibold text-primary mb-1">
              {{ scale.presentation.eyebrow }}
            </p>
            <h3
              [id]="'tiered-scale-' + result.platformId"
              class="h4 fw-bold mb-3"
            >
              {{ scale.presentation.heading }}
            </h3>
            <div
              *ngIf="scale.tiers[scale.selectedTierIndex] as selectedTier"
              class="row g-2"
            >
              <div class="col-12 col-md-4">
                <span class="small text-body-secondary d-block">
                  {{ scale.presentation.amountLabel }}
                </span>
                <strong>{{ tierAmountLabel(selectedTier) }}</strong>
              </div>
              <div class="col-6 col-md-4">
                <span class="small text-body-secondary d-block">
                  {{ scale.presentation.organizationRetainedLabel }}
                </span>
                <strong>
                  {{ formatPercentage(10000 - selectedTier.rateBasisPoints) }}
                </strong>
              </div>
              <div class="col-6 col-md-4">
                <span class="small text-body-secondary d-block">
                  {{ scale.presentation.feeShareLabel }}
                </span>
                <strong>
                  {{ formatPercentage(selectedTier.rateBasisPoints) }}
                </strong>
              </div>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th scope="col">{{ scale.presentation.amountLabel }}</th>
                  <th scope="col">
                    {{ scale.presentation.organizationRetainedLabel }}
                  </th>
                  <th scope="col">{{ scale.presentation.feeShareLabel }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let tier of scale.tiers; let index = index"
                  [class.table-primary]="index === scale.selectedTierIndex"
                  [attr.aria-current]="
                    index === scale.selectedTierIndex ? 'true' : null
                  "
                >
                  <th scope="row">{{ tierAmountLabel(tier) }}</th>
                  <td>{{ formatPercentage(10000 - tier.rateBasisPoints) }}</td>
                  <td>{{ formatPercentage(tier.rateBasisPoints) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="small text-body-secondary p-3 p-md-4 mb-0 border-top">
            {{ scale.presentation.note }}
          </p>
        </section>

        <section
          *ngIf="showConfigurationComparison"
          class="border-top pt-4 mt-4"
          [attr.aria-labelledby]="
            'configuration-comparison-' + result.platformId
          "
        >
          <h3
            [id]="'configuration-comparison-' + result.platformId"
            class="h4 fw-bold mb-2"
          >
            Compare configurations
          </h3>
          <p class="text-body-secondary mb-1">
            The vertical line marks
            {{ formatMoney(intendedDonationBaseline) }} in intended donations.
            Each marker shows how much the organization receives.
          </p>
          <p class="small text-body-secondary mb-4">
            Select a configuration to update the estimated result and detailed
            calculation.
          </p>

          <fieldset>
            <legend class="visually-hidden">
              Choose a {{ result.platformName }} configuration
            </legend>
            <div class="row g-3">
              <div
                *ngFor="let configuration of configurationResults"
                class="col-12 col-lg-6"
              >
                <input
                  class="btn-check"
                  type="radio"
                  autocomplete="off"
                  [id]="configuration.id"
                  [name]="result.platformId + '-configuration'"
                  [value]="configuration.id"
                  [checked]="configuration.selected"
                  (change)="selectConfiguration(configuration)"
                />
                <label
                  class="btn card h-100 w-100 text-start p-0 overflow-hidden"
                  [class.border-primary]="configuration.selected"
                  [class.border-2]="configuration.selected"
                  [class.shadow-sm]="configuration.selected"
                  [class.bg-primary-subtle]="configuration.selected"
                  [for]="configuration.id"
                >
                  <span class="card-body d-flex flex-column p-3 p-md-4">
                    <span
                      class="d-flex align-items-start justify-content-between gap-3 mb-3"
                    >
                      <span class="h5 fw-bold mb-0">
                        {{ configuration.label }}
                      </span>
                      <span
                        *ngIf="configuration.selected"
                        class="badge text-bg-primary d-inline-flex align-items-center gap-1"
                      >
                        <i
                          class="bi bi-check-circle-fill"
                          aria-hidden="true"
                        ></i>
                        Selected
                      </span>
                    </span>

                    <span
                      *ngIf="configuration.description"
                      class="small text-body-secondary mb-3"
                    >
                      {{ configuration.description }}
                    </span>

                    <span class="row g-3 align-items-end mb-2">
                      <span class="col-12 col-sm-7">
                        <span class="small text-body-secondary d-block mb-1">
                          Organization receives
                        </span>
                        <span class="h3 fw-bold d-block mb-0">
                          {{
                            formatMoney(configuration.organizationReceivesCents)
                          }}
                        </span>
                      </span>
                      <span class="col-12 col-sm-5 text-sm-end">
                        <span class="fw-semibold d-block">
                          {{
                            formatDelta(
                              configuration.organizationReceiveDeltaCents
                            )
                          }}
                        </span>
                        <span class="small text-body-secondary d-block">
                          vs. intended donations
                        </span>
                      </span>
                    </span>

                    <svg
                      class="w-100 d-block text-body-secondary mb-3"
                      width="100%"
                      height="46"
                      viewBox="0 0 100 18"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <line
                        [attr.x1]="baselineVisualizationPosition"
                        [attr.x2]="baselineVisualizationPosition"
                        y1="1"
                        y2="17"
                        stroke="currentColor"
                        stroke-width="1"
                        stroke-dasharray="2 2"
                        vector-effect="non-scaling-stroke"
                      ></line>
                      <line
                        [attr.x1]="configuration.visualizationStart"
                        [attr.x2]="configuration.visualizationEnd"
                        y1="9"
                        y2="9"
                        stroke="currentColor"
                        stroke-width="2"
                        vector-effect="non-scaling-stroke"
                      ></line>
                      <line
                        [attr.x1]="configuration.visualizationPosition"
                        [attr.x2]="configuration.visualizationPosition"
                        y1="7"
                        y2="11"
                        stroke="currentColor"
                        stroke-width="6"
                        stroke-linecap="round"
                        vector-effect="non-scaling-stroke"
                      ></line>
                    </svg>

                    <span class="row g-3 border-top pt-3 mt-auto">
                      <span class="col-12 col-sm-6">
                        <span class="small text-body-secondary d-block mb-1">
                          Organization pays
                        </span>
                        <span class="fw-bold d-block">
                          {{
                            formatMoney(
                              configuration.result.organizationCostCents
                            )
                          }}
                        </span>
                      </span>
                      <span class="col-12 col-sm-6">
                        <span class="small text-body-secondary d-block mb-1">
                          Supporters pay extra
                        </span>
                        <span class="fw-bold d-block">
                          {{ supporterExtraValue(configuration.result) }}
                        </span>
                      </span>
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </fieldset>
        </section>

        <app-calculation-details
          [result]="result"
          [scenario]="scenario"
          summaryLabel="View detailed calculation"
          (detailsOpened)="onDetailsOpened($event)"
          (pricingSourceOpened)="onPricingSourceOpened($event)"
        ></app-calculation-details>
      </section>

      <div
        class="d-flex flex-column flex-sm-row align-items-sm-center flex-wrap gap-2 mt-4 pt-4 border-top"
      >
        <a
          class="btn btn-primary"
          [href]="overviewUrl"
          (click)="onOverviewLinkClicked()"
        >
          Compare all fundraising platforms
        </a>
        <button
          type="button"
          class="btn btn-outline-primary"
          (click)="copyPlatformLink()"
        >
          Copy platform scenario link
        </button>
        <p class="small text-body-secondary mb-0" role="status">
          {{ copyStatus }}
        </p>
      </div>

      <p class="visually-hidden" aria-live="polite" aria-atomic="true">
        {{ liveAnnouncement }}
      </p>
    </ng-container>
  </div>
</div>
